import { useCallback, useEffect, useRef, useState } from "react";

const confirmationEvent = "interep:confirmation";

export function confirmAction(options) {
  const config =
    typeof options === "string" ? { message: options } : options || {};
  if (typeof window === "undefined") return Promise.resolve(false);
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent(confirmationEvent, {
        detail: {
          title: config.title || "Confirm action",
          message: config.message || "Do you want to continue?",
          confirmLabel: config.confirmLabel || "Continue",
          cancelLabel: config.cancelLabel || "Cancel",
          tone: config.tone === "danger" ? "danger" : "default",
          resolve,
        },
      }),
    );
  });
}

export function ConfirmationDialogHost() {
  const [request, setRequest] = useState(null);
  const requestRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const finish = useCallback((accepted) => {
    const current = requestRef.current;
    if (!current) return;
    requestRef.current = null;
    setRequest(null);
    current.resolve(Boolean(accepted));
    window.setTimeout(() => previousFocusRef.current?.focus?.(), 0);
  }, []);

  useEffect(() => {
    const open = (event) => {
      if (requestRef.current) requestRef.current.resolve(false);
      previousFocusRef.current = document.activeElement;
      requestRef.current = event.detail;
      setRequest(event.detail);
    };
    window.addEventListener(confirmationEvent, open);
    return () => {
      window.removeEventListener(confirmationEvent, open);
      if (requestRef.current) requestRef.current.resolve(false);
      requestRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!request) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelButtonRef.current?.focus();
    const keydown = (event) => {
      if (event.key === "Escape") finish(false);
    };
    window.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", keydown);
    };
  }, [finish, request]);

  if (!request) return null;
  return (
    <div
      className="intelex-confirm-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) finish(false);
      }}
    >
      <section
        className={`intelex-confirm-dialog is-${request.tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="intelex-confirm-title"
        aria-describedby="intelex-confirm-message"
      >
        <header>
          <span aria-hidden="true">!</span>
          <div>
            <h2 id="intelex-confirm-title">{request.title}</h2>
            <p id="intelex-confirm-message">{request.message}</p>
          </div>
        </header>
        <footer>
          <button
            type="button"
            className="intelex-secondary-button"
            ref={cancelButtonRef}
            onClick={() => finish(false)}
          >
            {request.cancelLabel}
          </button>
          <button
            type="button"
            className={
              request.tone === "danger"
                ? "intelex-danger-button"
                : "intelex-primary-button"
            }
            onClick={() => finish(true)}
          >
            {request.confirmLabel}
          </button>
        </footer>
      </section>
    </div>
  );
}
